// REPLACES App.js entirely
import { useState, useCallback, useMemo } from "react";
import "./App.css";
import FilterBar from "./FilterBar";
import CompensationHeader from "./CompensationHeader";
import RoleListTable from "./RoleListTable";
import CostBandTable from "./CostBandTable";
import RangeBarsChart from "./RangeBarsChart";
import DistributionChart from "./DistributionChart";
import InsightCard from "./InsightCard";
import MethodologyFooter from "./MethodologyFooter";
import ComparisonChart from "./ComparisonChart";
import RegionMap from "./RegionMap";
import RoleListTerritoryQuarterRow from "./RoleListTerritoryQuarterRow";
import JobDetailTerritoryQuarterRow from "./JobDetailTerritoryQuarterRow";
import JobSpecPanel from "./JobSpecPanel";
import { computeRangeInsights, computeDistributionInsights } from "./keyInsights";
import { useEffect } from "react";
import { getTimePeriods, getVerticalStacks } from "./api";
import { getSharedSelection, setSharedSelection, clearSharedSelection } from "./sharedSelection";
import StackPickerPopup from "./StackPickerPopup";
import { getNavFrom } from "./navSource";
import { useSearchParams } from "react-router-dom";
import { getDefaultQuarter } from "./FilterBar";

// FIXED 2026-08-05: was placeholder-only text, BLOCKED on DB access. Now
// computed for real from costBandData (see keyInsights.js) - the same rows
// already fetched once per role for Cost Band Detail/Range Bars/Distribution,
// no new endpoint or DB access needed. No old-code precedent for this
// feature exists (confirmed via grep across the whole old Angular codebase)
// - built fresh from the newer prototype dashboard's screenshots, same
// situation as the Distribution chart itself. See keyInsights.js for the
// per-bullet computation and its caveats (cost-zone clustering threshold in
// particular has no stakeholder-defined value - using a reasonable default,
// documented there).

// Fixed "as of" quarter shown in CompensationHeader's tag line - deliberately
// separate from FilterBar's live Quarter dropdown/getDefaultQuarter usage
// there (which drives what data actually loads). This is just a label:
// resolved once, from whatever the latest quarter in the time-periods list
// is, and never re-tracks a later manual Quarter change - confirmed with
// the user this header badge only needs "latest", not live tracking.
function useHeaderQuarterLabel() {
  const [label, setLabel] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getTimePeriods()
      .then((periods) => {
        if (cancelled) return;
        const def = getDefaultQuarter(periods);
        if (!def?.name) return;
        // API name format is "2026 Q1"; header copy uses "Q1 2026".
        const m = def.name.match(/^(\d{4})\s*Q(\d)$/);
        setLabel(m ? `Q${m[2]} ${m[1]}` : def.name);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return label;
}

export default function App() {
  const [filters, setFilters] = useState({});
  const headerQuarterLabel = useHeaderQuarterLabel();
  // The vertical lives in the URL (?stack=4), so it survives a refresh and
  // can be linked. See the popup note in the JSX below.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlStackId = searchParams.get("stack") ? Number(searchParams.get("stack")) : null;
  // ?sub= carries the Sub Stack. Job title is deliberately NOT in the URL:
  // on staging, leaving Comp and coming back keeps the vertical and the sub
  // stack but drops the job title, and we match that (her call, 18 Sep).
  const urlSubStackId = searchParams.get("sub") ? Number(searchParams.get("sub")) : null;

  // First-arrival stack popup: only when the URL carries no vertical AND the
  // user is not coming back from JD·Skills with a role in hand. Without the
  // second condition, Comp -> JD·Skills -> back to Comp popped the dialog
  // over a restore that was about to happen and discarded the role (seen
  // live). A jd-tagged shared selection IS an answer to "which roles?", so
  // the popup has nothing to ask.
  // Pages Comp shares a selection with. Arriving from one of them is a
  // continuation of work the user is already doing, so the popup must not
  // interrupt it - even if no vertical is set (e.g. Blended Roles was used
  // without one). Her rule, 18 Sep: "wherever there is two way, the popup
  // should not show". Trends and the Agent are not in the list; they share
  // nothing with Comp, so a first arrival there still gets asked.
  const TWO_WAY_WITH_COMP = ["/compare", "/jd-skills"];

  const [showStackPicker, setShowStackPicker] = useState(() => {
    // Describes the trip just taken; a refresh or a pasted link leaves it
    // null, so those still count as fresh arrivals.
    const cameFrom = getNavFrom();
    if (TWO_WAY_WITH_COMP.includes(cameFrom)) return false;
    return urlStackId == null && getSharedSelection()?.source !== "jd";
  });

  // Writes the vertical into the URL. `replace` so the rail's back button
  // steps between PAGES, not through every filter change on one page.
  const setUrlStack = useCallback((id, subId = null) => {
    // id 0 is the synthetic "All" option - that means NO stack, so the
    // param comes off entirely rather than becoming ?stack=0. Changing the
    // vertical also resets the cascade, so ?sub= goes with it - unless the
    // caller already knows the sub stack (search pick, JD·Skills restore).
    // FIXED 2026-09-25: both params are written in ONE call. Callers used to
    // follow this with setUrlSubStack(), whose functional update reads the
    // URL from before this write and put the OLD vertical back.
    const next = {};
    if (id) next.stack = String(id);
    if (subId) next.sub = String(subId);
    setSearchParams(next, { replace: true });
  }, [setSearchParams]);

  const setUrlSubStack = useCallback((id) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      // id 0 is "All Roles" - no sub stack, so no param.
      if (!id) next.delete("sub");
      else next.set("sub", String(id));
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Role clicked directly in RoleListTable - independent of FilterBar's own
  // Job Title dropdown, since a table click doesn't go through FilterBar's
  // internal state. Whichever happened most recently wins (see effects below).
  // Also passed DOWN into FilterBar as `syncRole` so it can resolve the same
  // Sub Stack (and show Clear Core Stack for a core role) that a manual
  // dropdown walk would have landed on - see FilterBar.jsx's syncRole effect.
  const [clickedRole, setClickedRole] = useState(null);
  const [roleListData, setRoleListData] = useState([]);
  // Which territories are selected on the role-list page - reported up from
  // RoleListTerritoryQuarterRow.jsx (see FIXED note below). Display-only
  // (RoleListTable's grouped-columns view) - no fetch depends on it.
  const [roleListTerritoryIds, setRoleListTerritoryIds] = useState([]);

  // FIXED 2026-08-05 (3rd territory/quarter attempt): Cost Band Detail data
  // used to be fetched independently by CostBandTable.jsx, RangeBarsChart.jsx
  // AND DistributionChart.jsx - three separate, redundant fetches all keyed
  // off Territory/Quarter, each racing the others depending on network
  // response order whenever those reset. Checked old code directly:
  // non-core-compensation.component.ts's loadData() is the ONLY fetch in the
  // whole old feature - the Cost Band Detail table and the Range Bars chart
  // are both plain @Input() territoryData, no fetch of their own at all
  // (compensation-range-chart.component.ts has zero HTTP calls). Fixed by
  // moving Territory + Quarter state (and the one fetch) into
  // JobDetailTerritoryQuarterRow.jsx (conditionally mounted below, matching
  // old's separate page-owned component instance), which reports the result
  // here once and it's passed down as a plain prop to all three consumers -
  // same as old's single @Input.
  const [costBandData, setCostBandData] = useState([]);
  const [costBandLoading, setCostBandLoading] = useState(false);
  const [costBandError, setCostBandError] = useState(null);
  // Reported up for CostBandTable's own secondary (region-accordion)
  // fetches and ComparisonChart's client-side territory filter - display/
  // secondary-fetch use only, not part of the top-level race fixed above.
  const [jobDetailQuarter, setJobDetailQuarter] = useState(null);
  const [jobDetailTerritoryIds, setJobDetailTerritoryIds] = useState([]);

  // Key Insights (Range Bars + Distribution cards) - recomputed only when
  // costBandData actually changes, from the same rows already loaded above.
  // See keyInsights.js for the per-bullet logic and its caveats.
  const rangeInsightPoints = useMemo(() => computeRangeInsights(costBandData), [costBandData]);
  const distributionInsightPoints = useMemo(() => computeDistributionInsights(costBandData), [costBandData]);

  // Which chart tab is active under Cost Band Detail - "range" or "distribution".
  // Both charts read the same costBandData now (see above) - switching tabs
  // is just a show/hide, no fetch triggered either way.
  const [chartTab, setChartTab] = useState("range");

  // FIXED 2026-08-05: which territory's row is expanded in the Cost Band
  // Detail table's region accordion - lifted up from CostBandTable (via its
  // onTerritoryExpand callback) so RegionMap, a sibling under a different
  // chart tab, can be driven by the same selection. Matches old Angular's
  // real region-osm-view, which reads selectedTerritoryId set by
  // toggleAccordion(item), not the top FilterBar Territory filter.
  const [expandedTerritoryId, setExpandedTerritoryId] = useState(null);

  const handleSelectionChange = useCallback((sel) => {
    setFilters(sel);
  }, []);

  // Reset the table-click selection whenever Vertical or Sub Stack changes,
  // matching FilterBar's own existing downstream-reset behavior.
  // FIXED 2026-08-05: FilterBar now clears clickedRole synchronously itself
  // (see clearClickedRole/onClearClickedRole below) for every path that goes
  // through its own handlers - these two effects are now a secondary safety
  // net for any path that doesn't (e.g. filters changing via some future
  // caller), not the primary defense anymore.
  useEffect(() => {
    setClickedRole(null);
  }, [filters.verticalStack, filters.subStack]);

  // If the user picks Job Title directly from FilterBar's own dropdown,
  // that takes over from any previous table click.
  useEffect(() => {
    if (filters.jobTitle) setClickedRole(null);
  }, [filters.jobTitle]);

  // Load role-list table whenever a Vertical is selected and no role is
  // active yet (neither via table click nor via the Job Title dropdown).
  const activeJobTitleId = clickedRole?.job_title_id ?? filters.jobTitle?.id ?? null;

  // Sub Stack reactivity: compensation_pivot.sql's is_all=TRUE bypasses the
  // is_core filter entirely and returns EVERY role for the vertical (it has
  // no sub_stack_id WHERE clause at all) - so scoping to one sub stack is a
  // client-side filter on the returned rows' own sub_stack_id, same as old
  // Angular's own client-side role-list filtering. "All Roles" (subStack.id
  // === 0) skips that filter and shows every role in the vertical.
  const subStackId = filters.subStack?.id ?? null;

  // FIXED 2026-08-05: the fetch itself now lives in
  // RoleListTerritoryQuarterRow.jsx (see that file's docstring) - this just
  // keeps the existing client-side sub-stack scoping unchanged, applied to
  // whatever raw rows that component reports.
  // ADDED 2026-08-20: old filters every role-list result down to ACTIVE
  // vertical stacks - `this.sidebarData = coreCompensationData.filter(x =>
  // this.verticalStacks.includes(x.verticalStackId))`, where verticalStacks
  // is `verticalStacks.filter(x => x.isActive).map(x => x.id)`
  // (core-compensation.component.ts:156-157, repeated in
  // filterSidebarData():453/456). We never ported that, so the table also
  // showed roles belonging to INACTIVE verticals - extra rows old doesn't
  // have, which is why the row list under All Roles never matched old.
  const [activeVerticalIds, setActiveVerticalIds] = useState(null);
  useEffect(() => {
    getVerticalStacks()
      .then((rows) => setActiveVerticalIds((rows || []).map((v) => v.id)))
      .catch(() => setActiveVerticalIds(null));
  }, []);

  const handleRoleListDataChange = useCallback(
    (rows) => {
      let scoped = subStackId != null && subStackId !== 0
        ? rows.filter((r) => r.sub_stack_id === subStackId)
        : rows;
      // null = the vertical list hasn't loaded yet; don't filter everything
      // away in that window (old can't hit this - it fetches both together).
      if (activeVerticalIds) {
        scoped = scoped.filter((r) => activeVerticalIds.includes(r.vertical_stack_id));
      }
      setRoleListData(scoped);
    },
    [subStackId, activeVerticalIds]
  );

  // FIXED 2026-08-05: replaces the three independent cost-band-detail
  // fetches previously in CostBandTable.jsx/RangeBarsChart.jsx/
  // DistributionChart.jsx - see the costBandData state note above.
  const handleCostBandDataChange = useCallback(({ data, loading, error }) => {
    setCostBandData(data);
    setCostBandLoading(loading);
    setCostBandError(error);
  }, []);

  // Quarter + Territory bar, page-scoped to match old exactly - see
  // FilterBar.jsx's row2Content docstring and RoleListTerritoryQuarterRow.jsx
  // / JobDetailTerritoryQuarterRow.jsx for the full explanation. Mutually
  // exclusive on activeJobTitleId, so React unmounts one and mounts the
  // other on every role-list <-> job-detail crossing - that unmount/remount
  // IS the reset, matching old's component-destroy/recreate exactly.
  const row2Content = activeJobTitleId ? (
    <JobDetailTerritoryQuarterRow
      jobTitleId={activeJobTitleId}
      onDataChange={handleCostBandDataChange}
      onQuarterChange={setJobDetailQuarter}
      onTerritoryIdsChange={setJobDetailTerritoryIds}
    />
  ) : (
    <RoleListTerritoryQuarterRow
      verticalStackId={filters.verticalStack?.id}
      subStackId={subStackId}
      onDataChange={handleRoleListDataChange}
      onTerritoryIdsChange={setRoleListTerritoryIds}
    />
  );

  const handleRoleClick = useCallback((row) => {
    setClickedRole(row);
  }, []);

  // FIXED 2026-08-05 (Clear Core Stack / Vertical-change table-disappears
  // bug): old Angular never has a separate "remembered clicked role" to go
  // stale - core-compensation.component.ts's row click and dropdown-
  // section.component.ts's Clear Core Stack both mutate the SAME shared
  // Config.stackModel object directly. Our port split that into two pieces
  // (this clickedRole state here, and FilterBar's own selectedSubStack/
  // selectedJobTitle), synced by FilterBar's syncRole effect - and that
  // effect could fire on a stale clickedRole BEFORE the two reactive
  // useEffects below (keyed on filters.verticalStack/subStack/jobTitle) had
  // a chance to clear it, re-asserting the just-cleared/just-changed role
  // one render late. Passed into FilterBar so Clear Core Stack and the
  // Vertical/Sub Stack/Job Title change handlers can clear clickedRole
  // SYNCHRONOUSLY, in the same click/change handler that clears FilterBar's
  // own local state - both updates land in the same React batch, so
  // syncRole already reflects null on the very next render and the effect
  // never gets a stale value to resurrect. The two effects below stay as a
  // secondary safety net for any other path.
  const clearClickedRole = useCallback(() => setClickedRole(null), []);

  // Header info: prefer the clicked row's data (has job_code + alternate
  // titles from GetCompensationPivot); fall back to FilterBar's own
  // jobTitle option if the user used the dropdown instead (job_code /
  // alternate titles only present there if JobTitle.to_dict() includes
  // them - see models.py note).
  const activeRoleName = clickedRole?.role ?? filters.jobTitle?.name ?? null;
  const activeJobCode = clickedRole?.job_code ?? filters.jobTitle?.job_code ?? null;
  const activeAlternateTitles = (
    clickedRole?.source_job_title_alternative ?? filters.jobTitle?.source_job_title_alternative ?? ""
  ).split(";").filter(Boolean);

  // Writes the active role to the shared in-memory selection (see
  // sharedSelection.js for why it is not persisted) - same shape as old's
  // and {verticalStackId, subStackId, jobTitleId} shape as old Angular's
  // Config.stackModel (see stack-model.ts's constructor reading this exact
  // key). This is how old Minerva carries "Finance > Finance Manager" from
  // the Recruitment/Comp page into Job Spec Comparison; JobSpecComparison.jsx
  // reads it back once on mount to pre-fill its own cascade and load the
  // 3-role ladder immediately (18/19-Aug discussion).
  //
  // FIXED 2026-08-19: was write-only ("only writes once a role is actually
  // active - an All/no-role Comp view leaves whatever was last selected
  // untouched") - confirmed by testing that this reads as a real bug, not
  // acceptable staleness: Comp sitting on All (no role picked) still showed
  // JD·Skills pre-filled with whatever role was last clicked, sometimes
  // sessions ago. All specifically now clears the shared selection so
  // JD·Skills correctly falls back to its own empty "select a job title"
  // state - this is a deliberate departure from old Minerva (which never
  // clears Config.stackModel either), not a missed port; the user
  // confirmed old's behavior here doesn't make sense for this app. Only
  // the All+no-role case clears it - picking a specific Vertical without
  // yet clicking a role still leaves the previous selection untouched,
  // unchanged from before.
  useEffect(() => {
    if (activeJobTitleId != null) {
      setSharedSelection({
        verticalStackId: clickedRole?.vertical_stack_id ?? filters.verticalStack?.id ?? null,
        subStackId: clickedRole?.sub_stack_id ?? filters.subStack?.id ?? null,
        jobTitleId: activeJobTitleId,
      });
      return;
    }
    if (filters.verticalStack?.id === 0) clearSharedSelection();
  }, [activeJobTitleId, clickedRole, filters.verticalStack, filters.subStack]);

  // FIXED 2026-08-05: quarter/territoryIds now come from
  // JobDetailTerritoryQuarterRow.jsx (see the costBandData state note above),
  // not from filters - merged in here for the components that still need
  // them (CostBandTable's own region-accordion fetches, ComparisonChart's
  // client-side territory filter).
  const jobDetailFilters = {
    ...filters,
    jobTitle: { id: activeJobTitleId },
    quarter: jobDetailQuarter,
    territoryIds: jobDetailTerritoryIds,
  };

  // NOTHING LOADS BEHIND THE POPUP (2026-09-18): while the stack popup is
  // up, the content area is deliberately empty - no FilterBar, no tables, no
  // charts. Rendering the unfiltered all-roles page behind a dialog the user
  // has not answered yet looked wrong (her call: "it should be blank"), and
  // it also fired every Comp query for a view about to be thrown away. The
  // page mounts only once a vertical is picked (or Skip is taken).
  if (showStackPicker) {
    return (
      <div className="page-body">
        <StackPickerPopup
          onPick={(id) => {
            setUrlStack(id);
            setShowStackPicker(false);
          }}
          onClose={() => setShowStackPicker(false)}
          onSkip={() => setShowStackPicker(false)}
          skipLabel="Skip - show all roles →"
        />
      </div>
    );
  }

  return (
    <div>
      <FilterBar
        key={urlStackId ?? "all"}
        urlStackId={urlStackId}
        urlSubStackId={urlSubStackId}
        onVerticalChange={setUrlStack}
        onSubStackChange={setUrlSubStack}
        onSelectionChange={handleSelectionChange}
        activeJobTitleId={activeJobTitleId}
        activeRoleName={activeRoleName}
        syncRole={clickedRole}
        onClearClickedRole={clearClickedRole}
        row2Content={row2Content}
        exportRows={costBandData}
      />

      <div className="page-body">
        {filters.verticalStack && (
          <CompensationHeader
            roleName={activeRoleName}
            jobCode={activeJobCode}
            alternateTitles={activeAlternateTitles}
            {...(headerQuarterLabel ? { quarterLabel: headerQuarterLabel } : {})}
          />
        )}

        {filters.verticalStack && !activeJobTitleId && (
          <RoleListTable
            data={roleListData}
            onRoleClick={handleRoleClick}
            highlightedRoleId={roleListData[0]?.job_title_id ?? null}
            territoryIds={roleListTerritoryIds}
          />
        )}

        {activeJobTitleId && (
          <CostBandTable
            filters={jobDetailFilters}
            data={costBandData}
            loading={costBandLoading}
            error={costBandError}
            onTerritoryExpand={setExpandedTerritoryId}
          />
        )}

        {activeJobTitleId && (
          <div className="chart-tabs">
            <button
              type="button"
              className={`chart-tab${chartTab === "range" ? " active" : ""}`}
              onClick={() => setChartTab("range")}
            >
              <span className="chart-tab-num">01</span>
              <span className="chart-tab-label">Range bars</span>
              <span className="chart-tab-sub">Cost Band Detail</span>
            </button>
            <button
              type="button"
              className={`chart-tab${chartTab === "distribution" ? " active" : ""}`}
              onClick={() => setChartTab("distribution")}
            >
              <span className="chart-tab-num">02</span>
              <span className="chart-tab-label">Distribution</span>
              <span className="chart-tab-sub">Fitted curves</span>
            </button>
            <button
              type="button"
              className={`chart-tab${chartTab === "comparison" ? " active" : ""}`}
              onClick={() => setChartTab("comparison")}
            >
              <span className="chart-tab-num">03</span>
              <span className="chart-tab-label">Comparison</span>
              <span className="chart-tab-sub">Vs CPI over time</span>
            </button>
            <button
              type="button"
              className={`chart-tab${chartTab === "region" ? " active" : ""}`}
              onClick={() => setChartTab("region")}
            >
              <span className="chart-tab-num">04</span>
              <span className="chart-tab-label">Region Map</span>
              <span className="chart-tab-sub">By territory</span>
            </button>
          </div>
        )}

        {activeJobTitleId && chartTab === "range" && (
          <>
            <RangeBarsChart filters={jobDetailFilters} data={costBandData} loading={costBandLoading} />
            <InsightCard
              eyebrow="Key insights · range bars"
              title="Reading the cost case for distributed hiring."
              points={rangeInsightPoints}
            />
          </>
        )}

        {activeJobTitleId && chartTab === "distribution" && (
          <>
            <DistributionChart filters={jobDetailFilters} data={costBandData} loading={costBandLoading} />
            <InsightCard
              eyebrow="Key insights · distribution"
              title="Reading the shape of pay outcomes."
              points={distributionInsightPoints}
            />
          </>
        )}

        {/* No InsightCard here - the reference dashboard.html only has
            insight cards for Range bars / Distribution, not Comparison. */}
        {activeJobTitleId && chartTab === "comparison" && (
          <ComparisonChart filters={jobDetailFilters} />
        )}

        {/* Needs a territory row expanded in the Cost Band Detail table
            above (Range bars tab) - same requirement as the real
            region-osm-view, which only ever received data for one expanded
            territory at a time. No InsightCard here either, same reasoning
            as Comparison. */}
        {activeJobTitleId && chartTab === "region" && (
          <RegionMap
            filters={jobDetailFilters}
            territoryId={expandedTerritoryId}
          />
        )}

        {/* Embedded Job Specification panel - ported from
            non-core-compensation.component.html's `<app-job-specification>`,
            which old shows unconditionally at the bottom of the job-detail
            page (not gated on any chart tab). Collapsed by default -
            JobSpecPanel itself lazy-loads on first expand. Placed above
            MethodologyFooter per explicit instruction. */}
        {activeJobTitleId && (
          <JobSpecPanel jobTitleId={activeJobTitleId} verticalStackId={filters.verticalStack?.id} />
        )}

        {/* Static, not per-tab - stays the same across Range bars / Distribution */}
        {activeJobTitleId && <MethodologyFooter />}
      </div>
    </div>
  );
}
